import React, { lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import ErrorBoundary from '@fs/zion-error-boundary'
import { targetEnv } from '@fs/zion-config'
import { AuthRoute, NotFound, Route, Switch, useLocation } from '@fs/zion-router'
import { Redirect } from 'react-router-dom'
import { NoticeLoading } from '@fs/zion-icon'
import { BrightspotRouting } from '@fs/zion-cms'
import { useFeatureFlag } from '@fs/zion-flags'
import NavigationTabs from './components/shared/SubNavigationBar'
import useNavLinks from './components/hooks/useNavLinks'
import QueuePermissionsProvider from './components/hooks/QueuePermissionsProvider'
import CanAddPersonalDataProvider from './components/hooks/CanAddPersonalDataProvider'

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const HelperResourcesPage = lazy(() => import('./components/consultant/helper/HelperResourcesPage'))
const FamilyHistoryNetworkPage = lazy(
  () => import('./components/consultant/family-history-network/FamilyHistoryNetworkPage')
)
const PlannerPage = lazy(() => import('./components/planner/PlannerPage'))
const ProfileOptInPage = lazy(() => import('./components/directory/ProfileOptInPage'))
const OptInPage = lazy(() => import('./components/planner/OptInPage'))
const HelpAndLearningPage = lazy(() => import('./components/helpcenter/learning/HelpAndLearningPage'))
const OneOnOnePage = lazy(() => import('./components/helpcenter/one-on-one/OneOnOnePage'))
const ClassesAndVideosPage = lazy(() => import('./components/helpcenter/classes-and-videos/ClassesAndVideosPage'))
const CommunityPage = lazy(() => import('./components/helpcenter/community/CommunityPage'))
const FullpageChatbot = lazy(() => import('./components/helpcenter/fullpage-chatbot/FullpageChatbot'))
const DevRouter = React.lazy(() => import('./components/dev/DevRouter'))
const ContactUsPage = React.lazy(() => import('./components/helpcenter/contact-us/ContactUsPage'))
const isProd = targetEnv === 'prod'

const queryClient = new QueryClient()

function App() {
  const { pathname } = useLocation()
  const navLinks = useNavLinks(pathname)
  const fullpageChatbotFlag = useFeatureFlag('help_helpCenter_fullPageChatbot')
  const brightspotSiteFlag = useFeatureFlag('help_helpCenter_zionCms')
  const contactUsFlag = useFeatureFlag('help_contactUsPage')
  return (
    <ErrorBoundary>
      <CanAddPersonalDataProvider>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<NoticeLoading />}>
            <NavigationTabs navLinks={navLinks} />
            <Switch>
              <Route exact path="/" component={() => 'Root page placeholder'} />
              <Route exact path="/helper" requirePersonalData>
                <QueuePermissionsProvider>
                  <HelperResourcesPage />
                </QueuePermissionsProvider>
              </Route>
              <Route path="/helper/planner/workwith" requirePersonalData>
                <FamilyHistoryNetworkPage />
              </Route>
              <AuthRoute exact path="/helper/planner" requirePersonalData>
                <PlannerPage />
              </AuthRoute>
              <AuthRoute
                path={['/planner/link', '/planner/link/:verificationCode', '/helper/planner/link']}
                requirePersonalData
                exact
              >
                <OptInPage />
              </AuthRoute>
              <AuthRoute path="/helper/profile-opt-in" component={ProfileOptInPage} requirePersonalData />
              <Route path={['/helpcenter/community-landing', '/help/helpcenter/community-landing']} requirePersonalData>
                <CommunityPage />
              </Route>
              <Route path={['/helpcenter/one-on-one', '/help/helpcenter/one-on-one']} requirePersonalData>
                <OneOnOnePage />
              </Route>
              <Route path={['/helpcenter/classes-and-videos', '/help/helpcenter/classes-and-videos']}>
                <ClassesAndVideosPage />
              </Route>
              {fullpageChatbotFlag.isOn && (
                <Route path={['/helpcenter/chat', '/help/helpcenter/chat']}>
                  <FullpageChatbot />
                </Route>
              )}
              {brightspotSiteFlag.isOn && (
                <Route
                  path={[
                    '/help/helpcenter/article',
                    '/helpcenter/article',
                    '/help/helpcenter/family-tree',
                    '/helpcenter/family-tree',
                    '/help/helpcenter/search',
                    '/helpcenter/search',
                  ]}
                  render={() => {
                    return <BrightspotRouting site={brightspotSiteFlag.config} />
                  }}
                />
              )}
              <Route path={['/helpcenter/landing', '/help/helpcenter/landing', '/helpcenter', '/help/helpcenter']}>
                <HelpAndLearningPage />
              </Route>
              {contactUsFlag.isOn && (
                <Route exact path={['/contact-us', '/help/contact-us']}>
                  <Redirect to="/contact-us/north-america" />
                </Route>
              )}
              {contactUsFlag.isOn && (
                <Route path={['/contact-us/:region', '/help/contact-us/:region']}>
                  <ContactUsPage />
                </Route>
              )}
              {/* Sample pages, hackathon projects, dev helpers, anything else that should be shown everywhere besides prod */}
              {isProd ? null : <Route path="/dev" component={DevRouter} />}
              {/* 404 fallback */}
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </QueryClientProvider>
      </CanAddPersonalDataProvider>
    </ErrorBoundary>
  )
}
export default App
